// ══════════════════════════════════════════════════════════════
// AGC Dashboard — Supabase Data Layer
// All reads/writes go through here. Falls back to localStorage
// if Supabase is unreachable (offline mode).
// ══════════════════════════════════════════════════════════════
import { supabase } from './supabase';

const LS = {
  transactions:    'agc_transactions',
  invoices:        'agc_invoices',
  importedPosIds:  'agc_imported_pos_ids',
  settings:        'agc_settings',
};

function lsGet(key, fallback = []) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

// ── snake_case ↔ camelCase helpers ───────────────────────────
function toDb(t) {
  return {
    id:             t.id,
    date:           t.date,
    direction:      t.direction,
    channel:        t.channel,
    payment_method: t.paymentMethod || '',
    category:       t.category,
    description:    t.description,
    qty:            t.qty,
    unit_price:     t.unitPrice,
    total_amount:   t.totalAmount,
    vendor:         t.vendor,
    metal:          t.metal,
    weight:         t.weight || 0,
    grade:          t.grade || '',
    grade_service:  t.gradeService || '',
    notes:          t.notes || '',
    invoice_number: t.invoiceNumber || '',
    invoice_id:     t.invoiceId || '',
    aureus_id:      t.aureusId || null,
    source:         t.source || 'manual',
  };
}
function fromDb(r) {
  return {
    id:            r.id,
    date:          r.date,
    direction:     r.direction,
    channel:       r.channel,
    paymentMethod: r.payment_method || '',
    category:      r.category,
    description:   r.description,
    qty:           parseFloat(r.qty) || 1,
    unitPrice:     parseFloat(r.unit_price) || 0,
    totalAmount:   parseFloat(r.total_amount) || 0,
    vendor:        r.vendor,
    metal:         r.metal,
    weight:        parseFloat(r.weight) || 0,
    grade:         r.grade || '',
    gradeService:  r.grade_service || '',
    notes:         r.notes || '',
    invoiceNumber: r.invoice_number || '',
    invoiceId:     r.invoice_id || '',
    aureusId:      r.aureus_id || null,
    source:        r.source || 'manual',
  };
}
function invToDb(inv) {
  return {
    id:             inv.id,
    invoice_number: inv.invoiceNumber || '',
    vendor:         inv.vendor || '',
    date:           inv.date || '',
    direction:      inv.direction || 'Purchase',
    channel:        inv.channel || 'Retail',
    payment_method: inv.paymentMethod || '',
    notes:          inv.notes || '',
    total:          inv.total || 0,
  };
}
function invFromDb(r) {
  return {
    id:            r.id,
    invoiceNumber: r.invoice_number || '',
    vendor:        r.vendor || '',
    date:          r.date || '',
    direction:     r.direction || 'Purchase',
    channel:       r.channel || 'Retail',
    paymentMethod: r.payment_method || '',
    notes:         r.notes || '',
    total:         parseFloat(r.total) || 0,
    createdAt:     r.created_at || '',
  };
}

// ══════════════════════════════════════════════════════════════
// TRANSACTIONS
// ══════════════════════════════════════════════════════════════
export async function fetchTransactions() {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('date', { ascending: false });
    if (error) throw error;
    const mapped = data.map(fromDb);
    lsSet(LS.transactions, mapped); // cache locally
    return mapped;
  } catch {
    return lsGet(LS.transactions, []);
  }
}

export async function upsertTransaction(t) {
  const row = toDb(t);
  // Update local cache immediately
  const cached = lsGet(LS.transactions, []);
  const idx = cached.findIndex(c => c.id === t.id);
  if (idx >= 0) cached[idx] = t; else cached.unshift(t);
  lsSet(LS.transactions, cached);
  // Sync to DB
  try {
    const { error } = await supabase.from('transactions').upsert(row);
    if (error) console.error('upsertTransaction:', error.message);
  } catch (e) { console.error('upsertTransaction offline:', e.message); }
}

export async function deleteTransaction(id) {
  const cached = lsGet(LS.transactions, []).filter(t => t.id !== id);
  lsSet(LS.transactions, cached);
  try {
    const { error } = await supabase.from('transactions').delete().eq('id', id);
    if (error) console.error('deleteTransaction:', error.message);
  } catch {}
}

// ══════════════════════════════════════════════════════════════
// INVOICES
// ══════════════════════════════════════════════════════════════
export async function fetchInvoices() {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    const mapped = data.map(invFromDb);
    lsSet(LS.invoices, mapped);
    return mapped;
  } catch {
    return lsGet(LS.invoices, []);
  }
}

export async function upsertInvoice(inv) {
  const row = invToDb(inv);
  const cached = lsGet(LS.invoices, []);
  const idx = cached.findIndex(c => c.id === inv.id);
  if (idx >= 0) cached[idx] = inv; else cached.unshift(inv);
  lsSet(LS.invoices, cached);
  try {
    const { error } = await supabase.from('invoices').upsert(row);
    if (error) console.error('upsertInvoice:', error.message);
  } catch {}
}

export async function deleteInvoice(id) {
  const cached = lsGet(LS.invoices, []).filter(i => i.id !== id);
  lsSet(LS.invoices, cached);
  try {
    const { error } = await supabase.from('invoices').delete().eq('id', id);
    if (error) console.error('deleteInvoice:', error.message);
  } catch {}
}

// ══════════════════════════════════════════════════════════════
// IMPORTED POS IDs
// ══════════════════════════════════════════════════════════════
export async function fetchImportedPosIds() {
  try {
    const { data, error } = await supabase
      .from('imported_pos_ids')
      .select('aureus_id');
    if (error) throw error;
    const ids = data.map(r => r.aureus_id);
    lsSet(LS.importedPosIds, ids);
    return ids;
  } catch {
    return lsGet(LS.importedPosIds, []);
  }
}

export async function addImportedPosId(aureusId) {
  const cached = [...new Set([...lsGet(LS.importedPosIds, []), aureusId])];
  lsSet(LS.importedPosIds, cached);
  try {
    const { error } = await supabase
      .from('imported_pos_ids')
      .upsert({ aureus_id: aureusId });
    if (error) console.error('addImportedPosId:', error.message);
  } catch {}
}

// ══════════════════════════════════════════════════════════════
// BACKUP / RESTORE (still supported for full export)
// ══════════════════════════════════════════════════════════════
export async function exportAllData() {
  const [transactions, invoices, importedPosIds] = await Promise.all([
    fetchTransactions(),
    fetchInvoices(),
    fetchImportedPosIds(),
  ]);
  return { transactions, invoices, importedPosIds, exportedAt: new Date().toISOString(), version: 2 };
}

export async function importAllData(data) {
  if (!data) return;
  const txns = data.transactions || [];
  const invs = data.invoices || [];
  // Upsert all in parallel batches
  for (let i = 0; i < txns.length; i += 50) {
    const batch = txns.slice(i, i + 50).map(toDb);
    try {
      await supabase.from('transactions').upsert(batch);
    } catch {}
  }
  for (let i = 0; i < invs.length; i += 50) {
    const batch = invs.slice(i, i + 50).map(invToDb);
    try {
      await supabase.from('invoices').upsert(batch);
    } catch {}
  }
  // Update local cache
  lsSet(LS.transactions, txns);
  lsSet(LS.invoices, invs);
}
